import {
  createWalletClient,
  custom,
  getAddress,
  isAddressEqual,
  recoverTypedDataAddress,
  type EIP1193Provider,
  type Hex,
} from "viem";
import { baseSepolia } from "viem/chains";

export type WalletTypedData = {
  domain: Record<string, unknown>;
  types: Record<string, unknown>;
  primaryType: string;
  message: Record<string, unknown>;
};

function firstWalletAccount(value: unknown): `0x${string}` {
  if (!Array.isArray(value) || typeof value[0] !== "string") {
    throw new Error("钱包没有返回当前活动账户，请重新连接钱包");
  }
  return getAddress(value[0]);
}

export async function assertActiveWalletAccount(
  provider: EIP1193Provider,
  expectedAddress: `0x${string}`,
): Promise<`0x${string}`> {
  const activeAddress = firstWalletAccount(
    await provider.request({ method: "eth_accounts" }),
  );
  if (!isAddressEqual(activeAddress, getAddress(expectedAddress))) {
    throw new Error("钱包当前账户已切换，请重新连接后再签名支付");
  }
  return activeAddress;
}

export async function signTypedDataForActiveWallet(
  provider: EIP1193Provider,
  expectedAddress: `0x${string}`,
  typedData: WalletTypedData,
  signTypedData: (value: WalletTypedData) => Promise<Hex>,
): Promise<Hex> {
  await assertActiveWalletAccount(provider, expectedAddress);
  const signature = await signTypedData(typedData);

  let deployedCode: unknown;
  try {
    deployedCode = await provider.request({
      method: "eth_getCode",
      params: [expectedAddress, "latest"],
    });
  } catch {
    deployedCode = undefined;
  }

  // Locally recover only a confirmed EOA's standard 65-byte signature.
  // Deployed ERC-1271 wallets and longer EIP-6492 signatures must be verified
  // by the facilitator against wallet bytecode and possible deployment data.
  if (signature.length === 132 && deployedCode === "0x") {
    let recoveredAddress: `0x${string}`;
    try {
      recoveredAddress = await recoverTypedDataAddress({
        ...typedData,
        signature,
      } as Parameters<typeof recoverTypedDataAddress>[0]);
    } catch {
      throw new Error("钱包返回的支付签名无法校验，请重新连接当前钱包后再试");
    }
    if (!isAddressEqual(recoveredAddress, getAddress(expectedAddress))) {
      throw new Error("钱包插件返回了其他账户的签名，请确认当前账户并关闭冲突的钱包插件后重试");
    }
  }
  return signature;
}

export function createVerifiedBrowserWalletSigner(
  provider: EIP1193Provider,
  address: `0x${string}`,
) {
  const walletClient = createWalletClient({
    account: address,
    chain: baseSepolia,
    transport: custom(provider),
  });
  return {
    address,
    signTypedData: (typedData: WalletTypedData) => signTypedDataForActiveWallet(
      provider,
      address,
      typedData,
      value => walletClient.signTypedData({
        account: address,
        ...value,
      } as Parameters<typeof walletClient.signTypedData>[0]),
    ),
  };
}
